import { useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { Box, Typography, Tabs, Tab, List, Paper } from "@mui/material";
import { styles } from "./stylesNotificationPanel";
import theme from "@/theme";
import {
  notificationApi,
  useGetNotificationsQuery,
  useReadAllNotificationMutation,
  useLazyGetInactivityQuery,
} from "@/store/api/notificationApi";
import { getToken } from "@/helpers/cookiesHelper";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useNotifications } from "@/hooks/useNotifications";
import CustomLoader from "../UI/CustomLoader/CustomLoader";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import {
  setNotificationState,
  setUnreadCount,
  setAwayInfo,
  setAwayBadge,
  type AwayEarnings,
} from "@/store/slice/notificationSlice";
import { useAppSelector } from "@/hooks/useAppSelector";
import Section from "./Section";
import WhileYouWereAway from "./component/WhileYouWereAway";

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const LIMIT = 20;

const formatInactiveDuration = (seconds: number): string => {
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
};

export type NotificationItemType = {
  uid: string;
  createdAt: string;
  [key: string]: any;
};

export default function NotificationPanel() {
  const token = getToken();
  const dispatch = useAppDispatch();
  const { isOpen: isNotificationOpen, awayInfo } = useAppSelector(
    (state) => state.notificationState
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [allItems, setAllItems] = useState<NotificationItemType[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const handleTabChange = (_e: React.SyntheticEvent, v: number) => {
    setTabValue(v);
    setPage(1);
    setAllItems([]);
    setHasMore(true);
    setHasFetchedOnce(false);
  };
  const notificationType: "all" | "follow" = tabValue === 0 ? "all" : "follow";

  // API query
  const { data: resp, isFetching, isLoading } = useGetNotificationsQuery(
    {
      page,
      limit: LIMIT,
      type: notificationType,
    },
    { skip: !token, refetchOnMountOrArgChange: true }
  );

  const totalItems = resp?.meta?.total ?? 0;
  useEffect(() => {
    if (!isFetching && resp && page === 1) {
      setHasFetchedOnce(true);
    }
  }, [isFetching, resp, page]);

  // WebSocket feed
  const { feed } = useNotifications(token as string);
  const [readAllNotification] = useReadAllNotificationMutation();

  const [fetchInactivity] = useLazyGetInactivityQuery();
  const [awayCard, setAwayCard] = useState<AwayEarnings | null>(null);
  const awayInfoRef = useRef(awayInfo);
  awayInfoRef.current = awayInfo;
  const markReadRef = useRef(false);
  useEffect(() => {
    if (!isNotificationOpen || !token) {
      markReadRef.current = false;
      return;
    }
    if (markReadRef.current || !resp || isFetching) return;
    markReadRef.current = true;
    (async () => {
      let away: AwayEarnings | null = awayInfoRef.current;
      const res = await fetchInactivity()
        .unwrap()
        .catch(() => undefined);
      if (res && res.totalEarnedWhileInactive > 0) {
        away = { amount: res.totalEarnedWhileInactive, seconds: res.totalInactiveSeconds };
      }
      setAwayCard(away && away.amount > 0 ? away : null);
      dispatch(setAwayInfo(null));
      dispatch(setAwayBadge(false));
      try {
        await readAllNotification({}).unwrap();
        dispatch(setUnreadCount(0));
      } catch (err) {
        console.error(err);
      }
    })();
  }, [isNotificationOpen, token, resp, isFetching, fetchInactivity, readAllNotification, dispatch]);

  useEffect(() => {
    if (!resp) return;
    const items: NotificationItemType[] = resp.data ?? [];

    setAllItems((prev) => {
      const prevByUid = new Map(prev.map((n) => [n.uid, n]));
      const map = new Map<string, NotificationItemType>();

      // Page 1: replace list entirely (fresh open / account switch / tab switch)
      // Page 2+: accumulate for infinite scroll
      if (page > 1) {
        prev.forEach((n) => map.set(n.uid, n));
      }
      items.forEach((n) => {
        const prior = prevByUid.get(n.uid);
        map.set(n.uid, prior && prior.read === false ? { ...n, read: false } : n);
      });
      feed.forEach((n) => map.set(n.uid, n));

      return Array.from(map.values()).sort(
        (a, b) => dayjs.utc(b.createdAt).valueOf() - dayjs.utc(a.createdAt).valueOf()
      );
    });
  }, [resp, feed]);

  useEffect(() => {
    setHasMore(allItems.length < totalItems && page * LIMIT < totalItems);
  }, [allItems, totalItems]);

  // Infinite scroll
  const isFetchingRef = useRef(isFetching);
  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);

  useEffect(() => {
    if (!hasMore) return;
    const el = loadMoreRef.current;
    if (!el) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingRef.current) {
          setPage((prev) => prev + 1);
        }
      },
      { root: scrollRef.current, threshold: 0.1 }
    );

    observer.current.observe(el);

    return () => observer.current?.disconnect();
  }, [hasMore]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        if (isNotificationOpen) dispatch(setNotificationState(false));
      }
    };
    if (isNotificationOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isNotificationOpen, dispatch]);

  // Clear notification cache on unmount so stale data never shows
  useEffect(() => {
    return () => {
      dispatch(notificationApi.util.resetApiState());
    };
  }, [dispatch]);

  const displayItems = allItems;

  const showAwayCard = tabValue === 0 && !!awayCard && awayCard.amount > 0;

  const { today, last7days, earlier } = useMemo(() => {
    const now = dayjs.utc();
    const last24h = now.subtract(24, "hour");
    const last7d = now.subtract(7, "day");

    return {
      today: displayItems.filter((n) => dayjs.utc(n.createdAt).isSameOrAfter(last24h)),
      last7days: displayItems.filter((n) => {
        const ts = dayjs.utc(n.createdAt);
        return ts.isSameOrAfter(last7d) && ts.isBefore(last24h);
      }),
      earlier: displayItems.filter((n) => dayjs.utc(n.createdAt).isBefore(last7d)),
    };
  }, [displayItems]);

  const isInitialLoading = isFetching || isLoading;

  return (
    <Paper sx={styles.wrapper} ref={panelRef}>
      <Box sx={styles.header}>
        <Typography variant="h3" color="#1A1D1F" sx={{ fontWeight: 600, fontSize: "16px" }}>
          Notification
        </Typography>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            ...styles.tabPanel,
            "& .MuiTabs-indicator": {
              width: tabValue === 1 ? "104px" : "46px",
            },
          }}
        >
          <Tab label="All" sx={{ ...styles.tabStyle(tabValue, 0), width: "46px" }} disableRipple />
          <Tab
            label="My Follows"
            sx={{ ...styles.tabStyle(tabValue, 1), width: "104px" }}
            disableRipple
          />
        </Tabs>
      </Box>

      <Box id="notification-scroll" sx={styles.notificationWrapper} ref={scrollRef}>
        <List sx={{ width: "100%", bgcolor: theme.palette.custom.mainWhite, p: 0, pb: "12px" }}>
          {!hasFetchedOnce ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "730px",
              }}
            >
              <CustomLoader loading size={42} />
            </Box>
          ) : (
            <>
              {showAwayCard && awayCard && (
                <Box sx={{ mb: "12px", mx: "16px" }}>
                  <WhileYouWereAway
                    info={{
                      duration: formatInactiveDuration(awayCard.seconds),
                      amount: awayCard.amount,
                      earnedUsd: awayCard.amount,
                    }}
                  />
                </Box>
              )}

              {today.length > 0 && <Section title="Today" items={today} />}
              {last7days.length > 0 && <Section title="Last 7 days" items={last7days} mt={2.75} />}
              {earlier.length > 0 && <Section title="Earlier" items={earlier} mt={2.75} />}

              {/* sentinel */}
              {hasMore && <div ref={loadMoreRef} style={{ height: 1 }} />}

              {isFetching && page > 1 && allItems.length !== 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CustomLoader loading size={24} />
                </Box>
              )}

              {!hasMore && allItems.length > 0 && (
                <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
                  No more notifications
                </Box>
              )}

              {hasFetchedOnce && !isInitialLoading && resp?.meta?.total === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "730px",
                  }}
                >
                  <Typography
                    component="span"
                    fontFamily="Inter"
                    fontWeight={500}
                    fontSize="14px"
                    lineHeight="20px"
                    letterSpacing={0}
                    color={theme.palette.custom.blueGray500}
                  >
                    Nothing to see here — yet
                  </Typography>
                </Box>
              )}
            </>
          )}
        </List>
      </Box>
    </Paper>
  );
}
